import React from 'react';
import { useTranslation } from 'react-i18next';
import SettingsBox from '@medflex/components/stories/SettingsBox';
import Button from '@medflex/components/stories/Button';
import Badge, { KINDS } from '@medflex/components/stories/Badge';
import { FEATURE_KEYS } from '@medflex/utils/subscriptionEnums';
import TextSnippetFile from '@medflex/components/stories/TextSnippetFile/TextSnippetFile';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import useFeatures from '../../hooks/useFeatures';
import useModal from '../../hooks/useModal';
import useSnippets from '../../hooks/useSnippets';

const sortByTitle = (a, b) => {
  if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
  if (b.title.toLowerCase() < a.title.toLowerCase()) return 1;
  return 0;
};

const TextSnippetsModule = () => {
  const { t } = useTranslation('physician');
  const { snippets } = useSnippets();
  const { open } = useModal();
  const [textSnippets, pamFeature] = useFeatures([FEATURE_KEYS.TEXT_SNIPPETS, FEATURE_KEYS.PAM]);

  return (
    <div data-component="TextSnippetsModule">
      <header className="mb-6 flex items-center">
        <h2 className="text-2xl font-semibold">{t('TextSnippetsModule.title')}</h2>
      </header>
      <p className="mb-12">{t('TextSnippetsModule.description', { allowHtml: true })}</p>
      {textSnippets && textSnippets.active ? (
        <div className="flex flex-col space-y-6">
          <Button kind="link" onClick={() => open({ modal: 'textSnippetsEdit' })}>
            <PlusCircleIcon className="w-6 h-6" />
            <p className="ml-2">{t('TextSnippetsModule.create')}</p>
          </Button>
          {snippets &&
            snippets.sort(sortByTitle).map(snippet => (
              <SettingsBox
                title={snippet.title}
                key={snippet.id}
                tags={pamFeature?.active && snippet?.quickReply ? snippet.tags : []}
                canEdit
                canDelete
                truncate
                onEdit={() => open({ modal: 'textSnippetsEdit', id: snippet.id })}
                onDelete={() => open({ modal: 'textSnippetsDelete', id: snippet.id })}
              >
                <p className="mb-2">{snippet.text}</p>
                {snippet.files?.length && (
                  <TextSnippetFile
                    mediaType={snippet.files[0].mediaType}
                    name={snippet.files[0].name}
                    additionalFiles={
                      snippet.files.length > 1 ? t('TextSnippetsModule.additionalFiles') : undefined
                    }
                  />
                )}
              </SettingsBox>
            ))}
        </div>
      ) : (
        <div className="flex">
          <Button
            kind="link"
            className="hover:no-underline"
            onClick={() => open({ modal: 'featureUpgradeSnippets' })}
          >
            <PlusCircleIcon className="w-6 h-6" />
            <p className="ml-2 hover:underline">{t('TextSnippetsModule.create')}</p>
            <Badge className="ml-2 leading-normal hover:no-underline" kind={KINDS.SIGNAL}>
              Upgrade
            </Badge>
          </Button>
        </div>
      )}
    </div>
  );
};

export default TextSnippetsModule;
